/**
 * Centralized logger
 *
 * Behavior:
 * - In production: `log`, `info`, `debug` are no-ops. `warn` and `error` still
 *   write to the console so production issues remain visible (and can be picked
 *   up by error-tracking services).
 * - In non-production: all levels pass through to the underlying `console`.
 *
 * Use this instead of calling `console.*` directly anywhere in the app.
 *
 * Examples:
 *   import { logger } from '@/utils/logger';
 *   logger.log('something happened', payload);
 *   logger.error('failed to do X', err);
 *
 *   // Optional scoped logger with a fixed prefix:
 *   const log = logger.scope('[Auth]');
 *   log.info('signed in');
 */
import config from '@/config';

type LogFn = (...args: unknown[]) => void;

interface Logger {
  log: LogFn;
  info: LogFn;
  debug: LogFn;
  warn: LogFn;
  error: LogFn;
  scope: (prefix: string) => Logger;
}

const noop: LogFn = () => {};

const isProduction = config.IS_PRODUCTION;

/* eslint-disable no-console -- this module is the single sanctioned wrapper around the console API */
const baseLog: LogFn = isProduction ? noop : (...args) => console.log(...args);
const baseInfo: LogFn = isProduction ? noop : (...args) => console.info(...args);
const baseDebug: LogFn = isProduction ? noop : (...args) => console.debug(...args);
const baseWarn: LogFn = (...args) => console.warn(...args);
const baseError: LogFn = (...args) => console.error(...args);
/* eslint-enable no-console */

const createLogger = (prefix?: string): Logger => {
  const apply = (fn: LogFn): LogFn =>
    prefix ? (...args: unknown[]) => fn(prefix, ...args) : (...args: unknown[]) => fn(...args);

  return {
    log: apply(baseLog),
    info: apply(baseInfo),
    debug: apply(baseDebug),
    warn: apply(baseWarn),
    error: apply(baseError),
    scope: (childPrefix: string) => createLogger(prefix ? `${prefix} ${childPrefix}` : childPrefix),
  };
};

export const logger: Logger = createLogger();

export default logger;
